import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoSelectOption
} from '@po-ui/ng-components';

import { RecursoRegra, RegraBase } from '../shared/regras-api.service';
import { RegrasListaBase } from '../shared/regras-lista.base';
import { RegrasSkeletonComponent, RegrasToolbarComponent } from '../shared/regras-toolbar';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** De-para de código de serviço e produto (tabela ZOL). */
export interface RegraProduto extends RegraBase {
  codigo?: string;
  tipo: string;
  chave: string;
  produto: string;
  descricao: string;
}

/**
 * Aba do de-para de serviço e produto (ZOL).
 *
 * Reúne três de-paras num cadastro só, separados pelo campo Tipo:
 *
 *   1 - código de serviço -> produto
 *   2 - equivalência do município de São Paulo
 *   3 - produto de frete do CT-e (chave S/N do Simples Nacional)
 *
 * A diferença que a tela precisa respeitar: no tipo 2 o campo de destino NÃO
 * guarda produto, e sim o código LC116 equivalente — por isso ali ele vira
 * texto livre em vez da busca no SB1, e o backend não valida contra o SB1.
 *
 * A tabela abre filtrada no tipo 1: as 446 linhas da equivalência de São Paulo
 * afogariam as 64 do de-para de serviço se viessem misturadas.
 */
@Component({
  selector: 'app-regras-produto',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule,
    RegrasToolbarComponent,
    RegrasSkeletonComponent,
    PoComboListaVisivelDirective
  ],
  templateUrl: './produto.html',
  styles: [`
    .regras-auditoria { color: var(--color-neutral-dark-70, #6e6e6e); }
    ::ng-deep .regras-form .po-row { margin-bottom: 1.5rem; }
    ::ng-deep .regras-form .po-row:last-child { margin-bottom: 0; }
  `]
})
export class RegrasProdutoComponent extends RegrasListaBase<RegraProduto> {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  protected readonly recurso: RecursoRegra = 'produto';
  protected readonly rotulo = 'o de-para de serviço e produto';

  private readonly fb = inject(FormBuilder);

  editing: RegraProduto | null = null;
  duplicando = false;

  /** Abre no tipo 1 — ver o comentário da classe. */
  filtroTipo = '1';

  readonly formGroup: FormGroup = this.fb.group({
    codigo: [''],
    tipo: ['1', Validators.required],
    chave: ['', Validators.required],
    produto: ['', Validators.required],
    descricao: [''],
    ativo: ['1', Validators.required]
  });

  readonly tipoOptions: PoSelectOption[] = [
    { label: 'Código de serviço para produto', value: '1' },
    { label: 'Equivalência do município de São Paulo', value: '2' },
    { label: 'Produto de frete do CT-e', value: '3' }
  ];

  readonly filtroTipoOptions: PoSelectOption[] = [
    { label: 'Todos os tipos', value: '' },
    ...this.tipoOptions
  ];

  get modalTitle(): string {
    if (this.duplicando) { return 'Duplicar de-para'; }
    return this.editing ? 'Editar de-para' : 'Novo de-para';
  }

  get tipoSelecionado(): string {
    return this.formGroup.get('tipo')?.value ?? '1';
  }

  /** No tipo 2 o destino é código LC116, não produto do SB1. */
  get destinoEhProduto(): boolean {
    return this.tipoSelecionado !== '2';
  }

  get rotuloDestino(): string {
    return this.destinoEhProduto ? 'Produto' : 'Código LC116 equivalente';
  }

  get rotuloChave(): string {
    if (this.tipoSelecionado === '2') { return 'Código do serviço no município de SP'; }
    if (this.tipoSelecionado === '3') { return 'Regime do emitente'; }
    return 'Código de serviço';
  }

  readonly columns: Array<PoTableColumn> = [
    {
      property: 'codigo', label: 'Código', width: '110px', type: 'link',
      action: (_: string, row: RegraProduto) => this.editar(row)
    },
    {
      property: 'tipo', label: 'Tipo', width: '230px', type: 'label',
      labels: [
        { value: '1', label: 'Serviço para produto', color: 'color-01' },
        { value: '2', label: 'Equivalência São Paulo', color: 'color-08' },
        { value: '3', label: 'Frete do CT-e', color: 'color-11' }
      ]
    },
    { property: 'chave', label: 'Código de origem', width: '200px' },
    { property: 'produto', label: 'Produto / equivalente', width: '190px' },
    {
      property: 'ativo', label: 'Situação', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Ativo', color: 'color-10' },
        { value: '2', label: 'Inativo', color: 'color-07' }
      ]
    },
    { property: 'descricao', label: 'Descrição' }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar', icon: 'an an-pencil-simple', action: (row: RegraProduto) => this.editar(row) },
    { label: 'Duplicar', icon: 'an an-copy', action: (row: RegraProduto) => this.duplicar(row) },
    {
      label: 'Inativar', icon: 'an an-lock', type: 'danger',
      action: (row: RegraProduto) => this.alterarSituacao(row, '2'),
      visible: (row: RegraProduto) => row.ativo === '1'
    },
    {
      label: 'Reativar', icon: 'an an-lock-open',
      action: (row: RegraProduto) => this.alterarSituacao(row, '1'),
      visible: (row: RegraProduto) => row.ativo === '2'
    }
  ];

  readonly modalPrimaryAction: PoModalAction = { action: () => this.salvar(), label: 'Salvar' };
  readonly modalSecondaryAction: PoModalAction = { action: () => this.formModal.close(), label: 'Cancelar' };

  protected override aoIniciar(): void {
    // O destino muda de natureza entre produto do SB1 e código LC116: manter o
    // valor anterior gravaria um produto onde se espera um código de serviço.
    this.formGroup.get('tipo')?.valueChanges.subscribe(() => {
      const produto = this.formGroup.get('produto');
      if (produto?.value) {
        produto.setValue('', { emitEvent: false });
      }
    });
  }

  protected override filtrosExtras(): Record<string, string> {
    return { tipo: this.filtroTipo };
  }

  protected corpo(regra: RegraProduto): Record<string, string> {
    const body: Record<string, string> = {
      tipo: regra.tipo,
      chave: regra.chave,
      produto: regra.produto,
      descricao: regra.descricao ?? '',
      ativo: regra.ativo
    };
    if (regra.codigo) { body['codigo'] = regra.codigo; }
    return body;
  }

  onFiltroTipo(valor: string): void {
    this.filtroTipo = valor ?? '';
    this.carregar();
  }

  abrirNova(): void {
    this.editing = null;
    this.duplicando = false;
    // Herda o tipo em foco na listagem: quem está olhando o de-para de São
    // Paulo quase sempre quer incluir mais uma linha dele.
    const tipo = this.filtroTipo || '1';
    this.formGroup.reset({ ...this.valoresPadrao(), tipo });
    this.formModal.open();
  }

  editar(regra: RegraProduto): void {
    this.editing = regra;
    this.duplicando = false;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra });
    this.formModal.open();
  }

  duplicar(regra: RegraProduto): void {
    this.editing = null;
    this.duplicando = true;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra, codigo: '' });
    this.formModal.open();
  }

  salvar(): void {
    const regra = this.formGroup.getRawValue() as RegraProduto;

    const erro = this.validar(regra);
    if (erro) {
      this.notif.warning(erro);
      return;
    }

    this.gravar(regra, !!this.editing?.codigo, () => this.formModal.close());
  }

  private validar(f: RegraProduto): string {
    if (!f.tipo) { return 'Informe o tipo de de-para.'; }
    if (!f.chave?.trim()) { return 'Informe o código de origem.'; }
    if (!f.produto?.trim()) {
      return this.destinoEhProduto ? 'Informe o produto.' : 'Informe o código equivalente.';
    }
    if (f.tipo === '3' && !['S', 'N'].includes(f.chave.trim().toUpperCase())) {
      return 'No produto de frete, o código de origem é S (Simples Nacional) ou N (regime normal).';
    }
    return '';
  }

  private valoresPadrao(): Partial<RegraProduto> {
    return { codigo: '', tipo: '1', chave: '', produto: '', descricao: '', ativo: '1' };
  }
}
